const calculateBrightnessFromRGB = (r: number, g: number, b: number) => {
  return (r * 299 + g * 587 + b * 114) / 1000;
};

/**
 * Regex to match rgb(r, g, b) or rgb(r,g,b) format.
 * Captures the three RGB components as separate groups.
 * @example 'rgb(255, 128, 0)'.match(RGB_REGEX) => ['rgb(255, 128, 0)', '255', '128', '0']
 */
export const RGB_REGEX = /^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/;

/**
 * Parses an rgb() color string and returns the RGB components.
 * @param rgb - RGB color string (e.g., 'rgb(255, 128, 0)')
 * @returns Object with r, g, b numeric values, or null if invalid format
 */
export const parseRgb = (rgb: string): {
  r: number;
  g: number;
  b: number;
} | null => {
  const match = rgb.match(RGB_REGEX);
  if (!match) return null;
  return {
    r: parseInt(match[1], 10),
    g: parseInt(match[2], 10),
    b: parseInt(match[3], 10)
  };
};

/**
 * Converts an rgb() color string to rgba() with the specified alpha.
 * @param rgb - RGB color string (e.g., 'rgb(255, 128, 0)')
 * @param alpha - Alpha value between 0 and 1
 * @returns RGBA color string, or original input if invalid format
 * @example rgbToRgba('rgb(255, 128, 0)', 0.6) => 'rgba(255, 128, 0, 0.6)'
 */
export const rgbToRgba = (rgb: string | undefined, alpha: number): string => {
  if (!rgb) return '';
  const match = rgb.match(RGB_REGEX);
  if (!match) return rgb;
  return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${alpha})`;
};

/** Matches a `#rrggbb` hex color (case-insensitive), capturing the 6 hex digits. */
export const HEX_COLOR_PATTERN = /^#([0-9a-f]{6})$/i;

/**
 * Parses a `#rrggbb` hex string into an `[r, g, b]` tuple. Returns `null` for any
 * malformed input so callers can fall back gracefully.
 * @example parseHexColor('#2e7d7a') => [46, 125, 122]
 */
export const parseHexColor = (hex: string): readonly [number, number, number] | null => {
  const match = HEX_COLOR_PATTERN.exec(hex.trim());
  if (!match) return null;
  const value = parseInt(match[1], 16);
  return [value >> 16 & 0xff, value >> 8 & 0xff, value & 0xff];
};
const channelToHex = (channel: number): string => channel.toString(16).padStart(2, '0');

/**
 * Formats an `[r, g, b]` tuple as a `#rrggbb` hex string.
 * @example rgbToHex([46, 125, 122]) => '#2e7d7a'
 */
export const rgbToHex = (rgb: readonly [number, number, number]): string => `#${channelToHex(rgb[0])}${channelToHex(rgb[1])}${channelToHex(rgb[2])}`;

/**
 * Thanks to https://gist.github.com/w3core/e3d9b5b6d69a3ba8671cc84714cca8a4
 * Calculate brightness value by RGB or HEX color.
 * @param color (String) The color value in RGB or HEX (for example: #000000 || #000 || rgb(0,0,0) || rgba(0,0,0,0))
 * @returns (Number) The brightness value (dark) 0 ... 255 (light)
 */
export const brightnessByColor = (color: string) => {
  const isHEX = color.indexOf('#') === 0;
  const isRGB = color.indexOf('rgb') === 0;
  if (isHEX) {
    const m = color.substr(1).match(color.length === 7 ? /(\S{2})/g : /(\S{1})/g);
    if (m) {
      const r = parseInt(m[0], 16);
      const g = parseInt(m[1], 16);
      const b = parseInt(m[2], 16);
      return calculateBrightnessFromRGB(r, g, b);
    }
  }
  if (isRGB) {
    const m = color.match(/(\d+){3}/g);
    if (m) {
      const r = parseInt(m[0]);
      const g = parseInt(m[1]);
      const b = parseInt(m[2]);
      return calculateBrightnessFromRGB(r, g, b);
    }
  }
  return null;
};

/**
 * Return which theme of color you should apply to an element so it contrast
 * well with the color passed in parameter.
 *
 * default: use the color that you want (default one if there is one)
 * dark: use any color that is in darker shades
 * light: use any color that is in lighter shades
 *
 * @param color : color to analyze and get what type of color you should use
 *                for a text displayed on a background color for example
 * @return 'dark' | 'light' | 'default'
 */
export const getContrastedColorTheme = (color?: string): 'dark' | 'light' | 'default' => {
  if (!color) {
    return 'default';
  }
  // Based on https://www.w3.org/TR/AERT/#color-contrast a good contrast difference is 125
  const brightness = brightnessByColor(color);
  return brightness && brightness > 125 ? 'dark' : 'light';
};

// Thanks to
// https://medium.com/@pppped/compute-an-arbitrary-color-for-user-avatar-starting-from-his-username-with-javascript-cd0675943b66
export const stringToHslColor = (str: string, s = 30, l = 80) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const h = hash % 360;
  return 'hsl(' + h + ', ' + s + '%, ' + l + '%)';
};

/**
 * Source: https://stackoverflow.com/questions/36721830/convert-hsl-to-rgb-and-hex
 */
export const hslToHex = (hsl: {
  h: number;
  s: number;
  l: number;
} | string) => {
  const parseHsl = (hsl: string): {
    h: number;
    s: number;
    l: number;
  } => {
    const parts = hsl.replace('hsl(', '').replace(')', '').trim().split(',');
    return {
      h: parseInt(parts[0]),
      l: parseInt(parts[1]),
      s: parseInt(parts[2])
    };
  };
  const {
    h,
    l: originL,
    s
  } = typeof hsl === 'string' ? parseHsl(hsl) : hsl;
  const l = originL / 100;
  const a = s * Math.min(l, 1 - l) / 100;
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0'); // convert to Hex and prefix "0" if needed
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};
const getContrastedTextColorFromRgbComponents = (rgb: number[], options: {
  darkColor?: string;
  lightColor?: string;
} = {}) => {
  const threshold = 130; /* about half of 256. Lower threshold equals more dark text on dark background  */
  const cBrightness = (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000;
  const {
    darkColor,
    lightColor
  } = options;
  return cBrightness > threshold ? darkColor || '#000000' : lightColor || '#ffffff';
};

/**
 * Gets the best color between white and black to contrast with another color
 * passed in param
 * Source: https://codepen.io/davidhalford/pen/AbKBNr
 */
export const getContrastedTextColor = (hex: string, options?: Parameters<typeof getContrastedTextColorFromRgbComponents>[1]) => {
  /*
  	From this W3C document: http://www.webmasterworld.com/r.cgi?f=88&d=9769&url=http://www.w3.org/TR/AERT#color-contrast
  	
  	Color brightness is determined by the following formula: 
  	((Red value X 299) + (Green value X 587) + (Blue value X 114)) / 1000
      
      I know this could be more compact, but I think this is easier to read/explain.
  	
  	*/
  function cutHex(h: string) {
    return h.charAt(0) === '#' ? h.substring(1, 7) : h;
  }
  function hexToR(h: string) {
    return parseInt(cutHex(h).substring(0, 2), 16);
  }
  function hexToG(h: string) {
    return parseInt(cutHex(h).substring(2, 4), 16);
  }
  function hexToB(h: string) {
    return parseInt(cutHex(h).substring(4, 6), 16);
  }
  const hRed = hexToR(hex);
  const hGreen = hexToG(hex);
  const hBlue = hexToB(hex);
  return getContrastedTextColorFromRgbComponents([hRed, hGreen, hBlue], options);
};
export const getContrastedTextColorFromRgb = (rgb: string | undefined, _options?: Parameters<typeof getContrastedTextColorFromRgbComponents>[1]) => {
  if (!rgb) {
    return '#ffffff';
  }
  const rgbComponents = rgb.substring(4, rgb.length - 1).replace(/ /g, '').split(',').map(Number);
  return getContrastedTextColorFromRgbComponents(rgbComponents);
};

// ─── WCAG 2.x contrast math ───────────────────────────────────────────────────
// sRGB linearisation constants (IEC 61966-2-1)
const SRGB_MAX = 255;
const SRGB_LINEAR_THRESHOLD = 0.04045;
const SRGB_LINEAR_SCALE = 12.92;
const SRGB_GAMMA_OFFSET = 0.055;
const SRGB_GAMMA_DIVISOR = 1.055;
const SRGB_GAMMA_EXPONENT = 2.4;
// Luminance coefficients (ITU-R BT.709 / WCAG 2.x)
const LUMA_R = 0.2126;
const LUMA_G = 0.7152;
const LUMA_B = 0.0722;
// Contrast ratio offset per WCAG 2.x formula
const CONTRAST_OFFSET = 0.05;
export const WCAG_AA_THRESHOLD = 4.5;

/** Convert an sRGB channel (0–255) to its linear-light value (IEC 61966-2-1). */
export function srgbToLinear(c: number): number {
  const s = c / SRGB_MAX;
  return s <= SRGB_LINEAR_THRESHOLD ? s / SRGB_LINEAR_SCALE : Math.pow((s + SRGB_GAMMA_OFFSET) / SRGB_GAMMA_DIVISOR, SRGB_GAMMA_EXPONENT);
}

/** Relative luminance per WCAG 2.x (range 0–1). */
export function relativeLuminance(r: number, g: number, b: number): number {
  return LUMA_R * srgbToLinear(r) + LUMA_G * srgbToLinear(g) + LUMA_B * srgbToLinear(b);
}

/** Contrast ratio between two relative luminances (≥ 1). */
export function contrastRatio(l1: number, l2: number): number {
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + CONTRAST_OFFSET) / (darker + CONTRAST_OFFSET);
}

/** Linear interpolation of a color channel value: from + (to − from) × ratio */
export function mixChannel(from: number, to: number, ratio: number): number {
  return Math.round(from + (to - from) * ratio);
}